import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Play, BarChart2, RefreshCw, Clock, Activity, AlertCircle } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif;
  padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 28px;
  padding-bottom: 20px; border-bottom: 1px solid #1e2030; flex-wrap: wrap;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer;
  padding: 8px; border-radius: 8px; transition: all 0.2s;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const TitleArea = styled.div`flex: 1; min-width: 200px;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #f43f5e, #fb923c);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const PageSub = styled.p`margin: 2px 0 0; font-size: 12px; color: #64748b;`;

const NavTabs = styled.div`display: flex; gap: 8px; margin-left: auto; flex-wrap: wrap;`;
const NavTab = styled.button`
  background: ${p => p.active ? 'rgba(244,63,94,0.15)' : 'transparent'};
  border: 1px solid ${p => p.active ? '#f43f5e' : '#1e2030'};
  color: ${p => p.active ? '#f43f5e' : '#64748b'};
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s;
  &:hover { border-color: #f43f5e; color: #f43f5e; }
`;

const CardGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; margin-bottom: 24px;
`;
const StatCard = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 16px; padding: 24px;
  animation: ${fadeIn} 0.4s ease;
  display: flex; flex-direction: column; gap: 8px;
`;
const StatTitle = styled.div`font-size: 13px; color: #94a3b8; font-weight: 600;`;
const StatValue = styled.div`font-size: 28px; font-weight: 800; color: #f8fafc;`;

const ActionBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  background: #f43f5e; color: #fff; border: none; padding: 12px 24px; border-radius: 12px;
  font-weight: 700; font-size: 14px; cursor: pointer; transition: all 0.2s;
  &:hover { background: #e11d48; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(244,63,94,0.3); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const ProgressContainer = styled.div`
  width: 100%; height: 6px; background: #1e2030; border-radius: 3px; margin: 15px 0; overflow: hidden;
`;
const ProgressBar = styled.div`
  height: 100%; background: linear-gradient(90deg, #f43f5e, #fb923c);
  width: ${p => p.progress}%; transition: width 0.4s ease;
`;
const SimBadge = styled.div`
  background: rgba(251, 146, 60, 0.1); color: #fb923c; border: 1px solid rgba(251, 146, 60, 0.3);
  padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; display: flex; align-items: center; gap: 6px;
`;

function VwapDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [account, setAccount] = useState(null);
  const [running, setRunning] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [lastRunTime, setLastRunTime] = useState(null);

  // Simulation State
  const [isSimulating, setIsSimulating] = useState(false);
  const [simConfig, setSimConfig] = useState(null); // { date, stocks: [{code, name}] }
  const [simAccount, setSimAccount] = useState({ balance: 10000000, totalInvested: 0 });
  const [simPositions, setSimPositions] = useState([]);
  const [simTimeIndex, setSimTimeIndex] = useState(0); // 0 to 78 (09:00 to 15:30 in 5m steps)
  const [simHistory, setSimHistory] = useState([]); // All virtual trades
  const [isSimComplete, setIsSimComplete] = useState(false);

  useEffect(() => {
    fetchAccount();
    
    // Check for Simulation Params
    const params = new URLSearchParams(location.search);
    if (params.get('mode') === 'simulate') {
      const date = params.get('date');
      const codes = params.get('stocks')?.split(',') || [];
      const names = params.get('names')?.split(',') || [];
      
      if (date && codes.length > 0) {
        setSimConfig({
          date,
          stocks: codes.map((code, i) => ({ code, name: names[i] || code }))
        });
        setIsSimulating(true);
        console.log(`%c 🕒 VWAP 시뮬레이션 모드 시작: ${date} %c 대상 종목 ${codes.length}개`, 
          'background:#fb923c; color:#fff; font-weight:bold; font-size:14px; padding:6px 12px; border-radius:6px;',
          'color:#fb923c; font-size:12px; margin-left:10px;');
      }
    }
  }, [location]);

  const getNextTradingDay = (dateStr) => {
    const y = parseInt(dateStr.substring(0, 4));
    const m = parseInt(dateStr.substring(4, 6)) - 1;
    const d = parseInt(dateStr.substring(6, 8));
    const date = new Date(y, m, d);
    
    do {
      date.setDate(date.getDate() + 1);
    } while (date.getDay() === 0 || date.getDay() === 6); // Skip weekends
    
    return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  };

  const handleNextDay = () => {
    if (!simConfig) return;
    
    const nextDate = getNextTradingDay(simConfig.date);
    setSimConfig(prev => ({ ...prev, date: nextDate }));
    setSimTimeIndex(0);
    setIsSimComplete(false);
    console.log(`%c ⏭️ 다음 영업일로 이동: ${nextDate} %c 보유 포지션 ${simPositions.length}개 유지`, 
      'background:#3b82f6; color:#fff; font-weight:bold; font-size:14px; padding:6px 12px; border-radius:6px;',
      'color:#3b82f6; font-size:12px; margin-left:10px;');
  };

  // Simulation Step Function
  const runSimStep = async () => {
    if (!simConfig || running || isSimComplete) return;
    
    setRunning(true);
    try {
      // 09:00 -> idx 0, 15:30 -> idx 78
      const startTime = 9 * 60; // 540m
      const currentTimeTotal = startTime + (simTimeIndex * 5);
      const h = Math.floor(currentTimeTotal / 60);
      const m = currentTimeTotal % 60;
      const virtualTime = `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
      
      const payload = {
        date: simConfig.date,
        virtualTime,
        targetStocks: simConfig.stocks,
        virtualAccount: simAccount,
        virtualPositions: simPositions
      };

      const data = await apiService.request('/vwap/simulate', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (data.success) {
        const { runLog } = data;
        logExecutionResult(runLog, true);
        
        // Update State
        setSimAccount(runLog.virtualAccount);
        setSimPositions(runLog.virtualPositions);
        if (runLog.actions && runLog.actions.length > 0) {
            setSimHistory(prev => [...runLog.actions, ...prev].slice(0, 50));
        }
        
        if (currentTimeTotal >= (15 * 60 + 30)) {
          setIsSimComplete(true);
          console.log('%c 🏁 시뮬레이션 종료', 'background:#10b981; color:#fff; font-weight:bold; font-size:14px; padding:6px 12px; border-radius:6px;');
        } else {
          setSimTimeIndex(prev => prev + 1);
        }
      }
    } catch (e) {
      console.error('Simulation Step Error:', e);
    } finally {
      setRunning(false);
    }
  };

  // Simulation Loop
  useEffect(() => {
    if (!isSimulating || isSimComplete || running) return;

    const timer = setTimeout(() => {
      runSimStep();
    }, 1000); // 1 second per step

    return () => clearTimeout(timer);
  }, [isSimulating, isSimComplete, simTimeIndex, running]);

  const logExecutionResult = (runLog, isSim = false) => {
    if (!runLog) return;
    
    // ── Header ──
    console.log(
      '%c ══════════════════════════════════════════════════════════ ',
      'background:#111827; color:#f43f5e; font-weight:bold; font-size:13px;'
    );
    console.log(
      `%c ${isSim ? '🤖 SIM' : '🔥 LIVE'} 실행 결과  %c ${new Date(runLog.timestamp).toLocaleString('ko-KR')}`,
      `background:${isSim ? '#fb923c' : '#f43f5e'}; color:#fff; font-weight:bold; font-size:13px; padding:4px 8px; border-radius:4px;`,
      'color:#94a3b8; font-size:11px;'
    );

    // ── 감시 대상 종목 ──
        console.log(
            `%c 📋 감시 대상 종목 (${runLog.targetStocks.length}개)`,
            'color:#fb923c; font-weight:bold; font-size:12px; margin-top:4px;'
          );
        console.table(runLog.targetStocks.map(s => ({ code: s.code, name: s.name })));

    // ── 보유 포지션 ──
    if (runLog.currentPositions && runLog.currentPositions.length > 0) {
      console.log(
        `%c 💼 보유 포지션 (${runLog.currentPositions.length}개)`,
        'color:#3b82f6; font-weight:bold; font-size:12px;'
      );
    }

    // ── 종목별 분석 결과 ──
    if (runLog.stockAnalysis && runLog.stockAnalysis.length > 0) {
      const analysisTable = runLog.stockAnalysis.map(a => ({
        종목: a.name || a.code,
        현재가: a.currentPrice?.toLocaleString(),
        VWAP: a.currentVwap?.toLocaleString(),
        '괴리율': a.vwapDiffPercent,
        보유: a.holding ? '✅' : '',
        상태: a.status,
        판정: a.action?.type || a.reason || '-'
      }));
      console.table(analysisTable);

      // 각 종목 액션 메시지
      runLog.stockAnalysis.forEach(a => {
        if (a.action?.message) {
          const color = a.action.type === 'BUY' ? '#10b981' : a.action.type === 'SELL' ? '#ef4444' : '#94a3b8';
          console.log(`%c   ${a.action.message}`, `color:${color}; font-size:11px;`);
        }
      });
    }

    // ── 매매 체결 ──
    if (runLog.actions && runLog.actions.length > 0) {
      console.log(
        `%c 🚨 매매 체결 내역 (${runLog.actions.length}건)`,
        'color:#f43f5e; font-weight:bold; font-size:13px;'
      );
      runLog.actions.forEach(action => {
        if (action.type === 'BUY') {
          console.log(`%c   ${action.message}`, 'color:#10b981; font-weight:bold; font-size:12px;');
        } else if (action.type === 'SELL') {
          console.log(`%c   ${action.message}`, 'color:#ef4444; font-weight:bold; font-size:12px;');
        }
      });
    }

    console.log(
      '%c ══════════════════════════════════════════════════════════ ',
      'background:#111827; color:#f43f5e; font-weight:bold; font-size:13px;'
    );
  };

  const fetchAccount = async () => {
    try {
      const data = await apiService.request('/vwap/account');
      if (data.success) {
        setAccount(data.account);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleRun = async (isAuto = false) => {
    setRunning(true);
    try {
      const data = await apiService.request('/vwap/run', { method: 'POST' });
      const runLog = data.runLog;

      if (runLog) {
        logExecutionResult(runLog, false);
      }

      if (!isAuto) alert('가상매매 엔진 1회 수동 실행 완료');
      fetchAccount(); // refresh balance
    } catch (e) {
      if (!isAuto) alert('오류 발생: ' + e.message);
      console.error('VWAP Engine Error:', e);
    } finally {
      setRunning(false);
    }
  };

  // Live Watcher (Only if not simulating)
  useEffect(() => {
    if (!isMonitoring || isSimulating) return;

    const interval = setInterval(async () => {
      const now = new Date();
      if (now.getDay() === 0 || now.getDay() === 6) return; // Weekends

      const h = now.getHours();
      const m = now.getMinutes();
      const isMarketHours = (h === 9 && m >= 5) || (h > 9 && h < 15) || (h === 15 && m <= 20);
      
      if (isMarketHours && m % 5 === 0) {
        const timeStr = `${h}:${m}`;
        if (lastRunTime !== timeStr && !running) {
          setLastRunTime(timeStr);
          await handleRun(true);
        }
      }
    }, 20000);

    return () => clearInterval(interval);
  }, [isMonitoring, isSimulating, lastRunTime, running]);

  const toggleMonitoring = () => {
    setIsMonitoring(prev => {
      const next = !prev;
      if (next) {
        console.log(
          '%c 🟢 VWAP 모니터링 감시 시작 %c 매 5분 간격으로 자동 실행됩니다.',
          'background:#10b981; color:#fff; font-weight:bold; font-size:14px; padding:6px 12px; border-radius:6px;',
          'color:#10b981; font-size:12px;'
        );
      } else {
        console.log(
          '%c 🔴 VWAP 모니터링 감시 중지',
          'background:#ef4444; color:#fff; font-weight:bold; font-size:14px; padding:6px 12px; border-radius:6px;'
        );
      }
      return next;
    });
  }

  const fmt = n => n != null ? Number(n).toLocaleString() : '0';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PageTitle>{isSimulating ? '🤖 VWAP 가상매매 시뮬레이션' : '🔥 당일 주도주 VWAP 눌림목 가상매매'}</PageTitle>
            {isSimulating && <SimBadge><Clock size={12} /> {simConfig.date} Simulation</SimBadge>}
          </div>
          <PageSub>
            {isSimulating 
              ? `${simConfig.date} 피봇 종목 대상 5분 간격 백테스트 중...` 
              : 'Livermore 전환점 종목 대상 5분봉 VWAP 지지/이탈 매매 (Front-driven)'}
          </PageSub>
        </TitleArea>
        <NavTabs>
          <NavTab active>대시보드</NavTab>
          <NavTab onClick={() => navigate('/labs/vwap/positions')}>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/vwap/history')}>매매내역</NavTab>
        </NavTabs>
      </TopBar>

      {isSimulating && (
        <StatCard style={{ marginBottom: 24, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fb923c', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Activity size={18} /> Simulation Progress
            </div>
            <div style={{ fontSize: 13, color: '#94a3b8' }}>
              Virtual Time: <span style={{ color: '#fff', fontWeight: 800 }}>
                {String(Math.floor((9*60 + simTimeIndex*5) / 60)).padStart(2,'0')}:
                {String((9*60 + simTimeIndex*5) % 60).padStart(2,'0')}
              </span> / 15:30
            </div>
          </div>
          <ProgressContainer>
            <ProgressBar progress={(simTimeIndex / 78) * 100} />
          </ProgressContainer>
          <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>
            * 5초마다 5분이 경과한 것으로 계산하여 백테스트를 진행합니다. 결과는 콘솔창에 출력됩니다.
          </p>
        </StatCard>
      )}

      <CardGrid>
        <StatCard>
          <StatTitle>{isSimulating ? '시뮬레이션 잔고 (Sim Balance)' : '가상 운용 잔고 (Total Balance)'}</StatTitle>
          <StatValue>₩{fmt(isSimulating ? simAccount.balance : account?.balance)}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>{isSimulating ? '시뮬레이션 투자액 (Sim Invested)' : '투자 중인 금액 (Invested)'}</StatTitle>
          <StatValue>₩{fmt(isSimulating ? simAccount.totalInvested : account?.totalInvested)}</StatValue>
        </StatCard>
        {!isSimulating ? (
          <StatCard style={{ justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <ActionBtn onClick={toggleMonitoring} style={{ background: isMonitoring ? '#10b981' : '#f43f5e' }}>
                <Play size={18} />
                {isMonitoring ? '감시 중지' : '모니터링 감시 시작'}
              </ActionBtn>
              <ActionBtn onClick={() => handleRun(false)} disabled={running} style={{ background: '#1e2030', border: '1px solid #374151' }}>
                <RefreshCw size={18} style={{ animation: running ? 'spin 1s linear infinite' : 'none' }} />
              </ActionBtn>
            </div>
            <p style={{ fontSize: 11, color: '#64748b', marginTop: 12 }}>
              {isMonitoring ? '✅ 브라우저를 켜두면 매 5분마다 감시가 자동 동작합니다.' : '감시 중지 상태입니다 (수동 버튼으로 1회 실행 가능)'}
            </p>
          </StatCard>
        ) : (
          <StatCard style={{ justifyContent: 'center', alignItems: 'center' }}>
             <SimBadge style={{ fontSize: 14, padding: '10px 20px' }}>
                {isSimComplete ? '✅ 시뮬레이션 완료' : (running ? '⏳ 분석 중...' : '▶️ 시뮬레이션 진행 중')}
             </SimBadge>
             {isSimComplete && (
               <div style={{ display: 'flex', gap: '8px', marginTop: 12 }}>
                 <ActionBtn onClick={handleNextDay} style={{ background: '#3b82f6' }}>
                    다음 영업일 계속하기
                 </ActionBtn>
                 <ActionBtn onClick={() => window.location.reload()} style={{ background: '#1e2030' }}>
                    초기화
                 </ActionBtn>
               </div>
             )}
          </StatCard>
        )}
      </CardGrid>

      {isSimulating && (
        <StatCard style={{ minHeight: '300px' }}>
          <StatTitle style={{ color: '#fb923c', borderBottom: '1px solid #1e2030', paddingBottom: '12px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={16} /> 시뮬레이션 매매 기록 (Simulation History)
          </StatTitle>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: '400px' }}>
            {simHistory.length === 0 ? (
              <div style={{ color: '#444', textAlign: 'center', paddingTop: '40px', fontSize: '14px' }}>
                아직 체결된 매매 내역이 없습니다. (VWAP 돌파/이탈 감시 중)
              </div>
            ) : (
                simHistory.map((hist, idx) => (
                    <div key={idx} style={{ 
                        padding: '12px', borderBottom: '1px solid #1e2030', 
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: hist.type === 'BUY' ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span style={{ 
                                    padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 800,
                                    background: hist.type === 'BUY' ? '#10b981' : '#ef4444', color: '#fff'
                                }}>
                                    {hist.type === 'BUY' ? '매수' : '매도'}
                                </span>
                                <span style={{ fontWeight: 700, fontSize: '14px' }}>{hist.name}</span>
                                <span style={{ fontSize: '11px', color: '#666' }}>({hist.code})</span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                                {hist.shares.toLocaleString()}주 @ {hist.price?.toLocaleString() || hist.sellPrice?.toLocaleString()}원
                                {hist.reason && ` | ${hist.reason}`}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '12px', fontWeight: 800, color: '#fb923c', marginBottom: '4px' }}>
                                🕒 {hist.virtualTime ? `${hist.virtualTime.substring(0,2)}:${hist.virtualTime.substring(2,4)}` : ''}
                            </div>
                            {hist.type === 'SELL' && (
                                <div style={{ fontSize: '14px', fontWeight: 800, color: hist.profit > 0 ? '#ef4444' : '#3b82f6' }}>
                                    {hist.profit > 0 ? '+' : ''}{hist.profit.toLocaleString()} ({hist.returnRate})
                                </div>
                            )}
                        </div>
                    </div>
                ))
            )}
          </div>
        </StatCard>
      )}
    </Page>
  );
}

export default VwapDashboardPage;
